import { DateTime } from 'luxon';
import { z } from 'zod';

// ─── minors: age model ───────────────────────────────────────────────────────
//
// Server-side age computation for minor handling. Clients never compute age
// or receive a DOB back; they consume the derived `MinorStatus` from the
// registration profile view. A DOB is a zoneless calendar date; "today" is the
// calendar day of the `on` instant IN ITS OWN ZONE, so callers MUST pass a
// member-local DateTime (ADR-095 `resolveTimeZone`). Bucketing the day in UTC
// would release every US member up to a day early — the unsafe direction for
// a minor gate.

/**
 * USPS state code → age of majority, for the states that differ from the
 * national default. Mirrors the legacy backend table (unchanged since the
 * minors program launched): Alabama and Nebraska 19, Mississippi 21.
 */
export const AGE_OF_MAJORITY_BY_STATE: Readonly<Record<string, number>> = Object.freeze({
  AL: 19,
  MS: 21,
  NE: 19,
});

export const DEFAULT_AGE_OF_MAJORITY = 18;

/**
 * Age of majority for a free-text state value. Tolerant by design: the
 * profile's `state` column is member-entered text, so anything that does not
 * normalize to a known exception code falls back to the default of 18 — an
 * unknown state must never throw or block.
 */
const EXCEPTION_STATE_NAMES: Readonly<Record<string, string>> = Object.freeze({
  ALABAMA: 'AL',
  MISSISSIPPI: 'MS',
  NEBRASKA: 'NE',
});

export function ageOfMajorityForState(state: string | null | undefined): number {
  if (!state) return DEFAULT_AGE_OF_MAJORITY;
  const normalized = state.trim().toUpperCase();
  // The wire and roster state values are free text with only client-side
  // normalisation, so the full names of the exception states must resolve
  // too — every other value (any spelling of the 18-majority states, or
  // garbage) correctly lands on the default.
  const code = EXCEPTION_STATE_NAMES[normalized] ?? normalized;
  return AGE_OF_MAJORITY_BY_STATE[code] ?? DEFAULT_AGE_OF_MAJORITY;
}

/**
 * Whole years elapsed between an ISO calendar DOB and the calendar day of
 * `on` in its own zone (the caller passes a member-local instant, ADR-095). A
 * member attains an age on their birthday; a Feb-29 DOB attains its age on
 * Mar 1 of a non-leap year. An unparseable DOB returns `null` — callers treat
 * that as status unknown, never as adult.
 */
export function ageInYears(dateOfBirthIso: string, on: DateTime): number | null {
  const dob = DateTime.fromISO(dateOfBirthIso, { zone: 'utc' });
  if (!dob.isValid || !on.isValid) return null;
  // "Today" is the calendar day of `on` in its OWN zone — the caller passes a
  // member-local instant, so a member is never aged up before their local
  // birthday (the unsafe direction for a minor gate).
  const today = { year: on.year, month: on.month, day: on.day };
  if (
    dob.year > today.year ||
    (dob.year === today.year &&
      (dob.month > today.month || (dob.month === today.month && dob.day > today.day)))
  ) {
    return null;
  }
  // Feb-29 DOBs attain their age on Mar 1 in a non-leap year — the strict
  // direction (a day later, never earlier), so a minor is never released a
  // day early by calendar clamping.
  const nonLeapYear = !DateTime.utc(today.year, 1, 1).isInLeapYear;
  const [birthdayMonth, birthdayDay] =
    dob.month === 2 && dob.day === 29 && nonLeapYear ? [3, 1] : [dob.month, dob.day];
  const beforeBirthday =
    today.month < birthdayMonth || (today.month === birthdayMonth && today.day < birthdayDay);
  return today.year - dob.year - (beforeBirthday ? 1 : 0);
}

/**
 * The staff display rule for "minor": under 18 today in every state. It is
 * deliberately NOT `ageOfMajorityForState` — the state table governs consent
 * and hold logic, while staff surfaces mark a member as a minor until their
 * 18th birthday wherever they live, and the marker drops off on that day.
 */
export const MINOR_DISPLAY_AGE = 18;

/**
 * Whether a member is under {@link MINOR_DISPLAY_AGE} on the calendar day of
 * `now` in its own zone (pass a member-local instant, ADR-095). `null` when
 * there is no DOB or it does not parse — status unknown, never adult.
 */
export function isUnder18Today(dateOfBirth: string | null, now: DateTime): boolean | null {
  if (!dateOfBirth) return null;
  const age = ageInYears(dateOfBirth, now);
  return age === null ? null : age < MINOR_DISPLAY_AGE;
}

export const MINOR_STATUS = ['unknown', 'under_min_age', 'minor', 'adult'] as const;
export const minorStatusSchema = z.enum(MINOR_STATUS);
export type MinorStatus = z.infer<typeof minorStatusSchema>;

export interface DeriveMinorStatusInput {
  /** ISO calendar date, or null when no DOB is on file anywhere. */
  dateOfBirth: string | null;
  /** Free-text state (profile or roster candidate), or null when unknown. */
  state: string | null;
  /**
   * One entry per active program WITH A PARSEABLE CONFIG: its `min_age`
   * floor, or null when the program declares none. An absent floor counts as
   * 0, so one floorless program — or an empty catalog — makes `under_min_age`
   * unreachable (fail-open to `minor`/`adult`, never to a block). Unparseable
   * catalog rows are SKIPPED by the reader, never surfaced as floorless, so a
   * single bad row cannot silently disable the gate for everyone.
   */
  programFloors: ReadonlyArray<number | null>;
  /** Member-local instant (ADR-095 resolved zone), never a bare UTC now. */
  now: DateTime;
}

/**
 * The one decision function behind both the registration profile view's
 * `minorStatus` and the sticky enrolment stamp. Fail-closed on identity
 * (no/invalid DOB → `unknown`), fail-open on catalog data (missing floors
 * never block).
 */
export function deriveMinorStatus(input: DeriveMinorStatusInput): MinorStatus {
  if (!input.dateOfBirth) return 'unknown';
  const age = ageInYears(input.dateOfBirth, input.now);
  if (age === null) return 'unknown';
  const floors = input.programFloors.map((floor) => floor ?? 0);
  const underEveryFloor = floors.length > 0 && floors.every((floor) => age < floor);
  if (underEveryFloor) return 'under_min_age';
  return age < ageOfMajorityForState(input.state) ? 'minor' : 'adult';
}
